"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";

/** Minimal serializable figure shape the switcher needs. Pass it in from a
 *  server layout (the section's figure registry) so this client component never
 *  imports a `_lib/data` module — those transitively pull `artefact.ts`/
 *  `node:fs`, which can't live in a client bundle. */
export type SwitcherFigure = {
  id: string;
  num: string;
  href: string;
  title: string;
};

/**
 * Figure-to-figure switcher for a section's figure/spoke pages. Replaces the
 * anti-pattern where every spoke replayed the full section landing hero (the
 * chart sat ~1000px down the page). A light index rail: each figure as a
 * numbered chip with the active one highlighted + scrolled into view, flanked
 * by prev/next so the set reads sequentially. The figure's own title is
 * rendered by its <FigureFrame>, so this carries movement, not a second copy of
 * the heading. Deliberately distinct from the section spine (numbered chips,
 * not word tabs) so the two bars don't read as duplicates.
 *
 * First adopted by /water (figures); generalized for reuse across sections.
 */
export function FigureSwitcher({
  figures,
  accent,
  ariaLabel = "Figures",
}: {
  figures: SwitcherFigure[];
  accent: string;
  ariaLabel?: string;
}) {
  const pathname = usePathname();
  const idx = figures.findIndex((f) => f.href === pathname);
  const activeRef = useRef<HTMLAnchorElement | null>(null);

  // Keep the current figure visible when the rail overflows horizontally.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ inline: "center", block: "nearest" });
  }, [pathname]);

  const prev = idx > 0 ? figures[idx - 1] : null;
  const next = idx >= 0 && idx < figures.length - 1 ? figures[idx + 1] : null;

  const arrow =
    "shrink-0 cs-mono text-[14px] leading-none px-2 py-2 rounded transition-colors";

  return (
    <nav
      aria-label={ariaLabel}
      className="border-b border-rule bg-bg/90 backdrop-blur-md"
    >
      <div className="px-4 sm:px-8">
        <div className="mx-auto max-w-[1400px] flex items-center gap-2 py-2">
          {prev ? (
            <Link
              href={prev.href}
              rel="prev"
              title={`Previous · ${prev.title}`}
              className={`${arrow} text-ink-3 hover:text-ink hover:bg-bg-2`}
            >
              ←
            </Link>
          ) : (
            <span aria-hidden className={`${arrow} text-ink-3/30`}>
              ←
            </span>
          )}

          <ul className="flex items-center gap-1 overflow-x-auto -mx-1 px-1">
            {figures.map((f) => {
              const active = f.href === pathname;
              return (
                <li key={f.id}>
                  <Link
                    ref={active ? activeRef : undefined}
                    href={f.href}
                    aria-current={active ? "page" : undefined}
                    className="flex items-baseline gap-2 whitespace-nowrap rounded-full px-3 py-1.5 transition-colors hover:bg-bg-2"
                    style={
                      active
                        ? {
                            background: `color-mix(in srgb, ${accent} 14%, var(--surface))`,
                          }
                        : undefined
                    }
                  >
                    <span
                      className="cs-num text-[13px] leading-none"
                      style={{ color: active ? accent : "var(--ink-3)" }}
                    >
                      {f.num}
                    </span>
                    <span
                      className="text-[13px] leading-none"
                      style={{
                        color: active ? "var(--ink)" : "var(--ink-3)",
                        fontWeight: active ? 600 : 400,
                      }}
                    >
                      {f.title}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>

          {next ? (
            <Link
              href={next.href}
              rel="next"
              title={`Next · ${next.title}`}
              className={`${arrow} ml-auto text-ink-3 hover:text-ink hover:bg-bg-2`}
            >
              →
            </Link>
          ) : (
            <span aria-hidden className={`${arrow} ml-auto text-ink-3/30`}>
              →
            </span>
          )}
        </div>
      </div>
    </nav>
  );
}
